import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { runService } from '../services';
import { Grade } from '../types';
import { LoadingSpinner, Card, Button, Alert } from '../components/ui';

export function GradeReport() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  
  const [grade, setGrade] = useState<Grade | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const runIdNum = runId ? parseInt(runId, 10) : null;

  const loadGrade = useCallback(async () => {
    if (!runIdNum) return;
    
    try {
      const gradeData = await runService.getGradeReport(runIdNum);
      setGrade(gradeData);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load grade:', err);
      if (err.response?.status === 404) {
        setError('Grade report not found.');
      } else if (err.response?.status === 403) {
        setError('Access denied. You can only view your own grade reports.');
      } else {
        setError('Failed to load grade report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [runIdNum]);

  useEffect(() => {
    loadGrade();
  }, [loadGrade]);

  if (loading) {
    return (
      <div className="p-8 text-white bg-gray-900 min-h-screen">
        <div className="flex items-center justify-center mt-20">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !grade) {
    return (
      <div className="p-8 text-white bg-gray-900 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <Alert variant="error" className="mb-6">
            {error || 'Grade report not found'}
            <Button 
              onClick={() => navigate(`/runs/${runId}`)} 
              className="ml-4"
              size="sm"
            >
              Back to Lab
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  const passPercentage = Math.round((grade.score / grade.total_points) * 100);
  const isPassing = passPercentage >= 70;

  return (
    <div className="p-8 text-white bg-gray-900 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate(`/runs/${runId}`)} 
            className="text-blue-400 hover:text-blue-300 mb-4 inline-flex items-center"
          >
            ← Back to Lab
          </button>
        </div>

        {/* Grade Summary */}
        <Card className="bg-gray-800 border-gray-700 mb-8">
          <div className="p-8">
            <h1 className="text-3xl font-bold mb-6">Grade Report</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className={`text-5xl font-bold ${isPassing ? 'text-green-400' : 'text-red-400'}`}>
                  {passPercentage}%
                </div>
                <div className="text-gray-400 mt-2">Overall Score</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-semibold text-white">
                  {grade.score}/{grade.total_points}
                </div>
                <div className="text-gray-400 mt-2">Points Earned</div>
              </div>
              {grade.report.summary && (
                <div className="text-center">
                  <div className="text-3xl font-semibold text-white">
                    {grade.report.summary.passed_checks}/{grade.report.summary.total_checks}
                  </div>
                  <div className="text-gray-400 mt-2">Checks Passed</div>
                </div>
              )}
            </div>
            <div className="mt-6 text-center text-sm text-gray-500">
              Graded on {new Date(grade.created_at).toLocaleString()}
            </div>
          </div>
        </Card>

        {/* Detailed Check Results */}
        <h2 className="text-2xl font-semibold mb-4">Check Results</h2>
        <div className="space-y-4">
          {grade.report.checks && grade.report.checks.map((check) => (
            <Card 
              key={check.id} 
              className={`border-2 ${
                check.passed 
                  ? 'bg-green-900/10 border-green-500/50' 
                  : 'bg-red-900/10 border-red-500/50'
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className={`text-2xl mt-1 ${
                      check.passed ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {check.passed ? '✅' : '❌'}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-white mb-1">
                        {check.description}
                      </h3>
                      <p className="text-sm text-gray-400">
                        Check ID: {check.id}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xl font-bold ${
                      check.passed ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {check.points_earned || 0}/{check.total_points || 0} pts
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Execution time: {(check.execution_time * 1000).toFixed(0)}ms
                    </div>
                  </div>
                </div>
                
                {!check.passed && (
                  <div className="mt-4 space-y-3 border-t border-gray-700 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {check.expected_value && (
                        <div>
                          <span className="text-sm text-gray-400 block mb-1">What the grader expected:</span>
                          <div className="bg-gray-800 rounded p-3">
                            <code className="text-green-300 text-sm whitespace-pre-wrap break-words">
                              {check.expected_value}
                            </code>
                          </div>
                        </div>
                      )}
                      {check.actual_value && (
                        <div>
                          <span className="text-sm text-gray-400 block mb-1">What the grader found:</span>
                          <div className="bg-gray-800 rounded p-3">
                            <code className="text-red-300 text-sm whitespace-pre-wrap break-words">
                              {check.actual_value}
                            </code>
                          </div>
                        </div>
                      )}
                    </div>
                    {check.error_message && (
                      <div>
                        <span className="text-sm text-gray-400 block mb-1">Error Details:</span>
                        <div className="bg-red-900/20 border border-red-700/50 rounded p-3">
                          <code className="text-red-200 text-sm">
                            {check.error_message}
                          </code>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {check.passed && check.actual_value && check.actual_value !== check.expected_value && (
                  <div className="mt-4 border-t border-gray-700 pt-4">
                    <div className="bg-green-900/20 border border-green-700/50 rounded p-3">
                      <span className="text-green-200 text-sm">
                        ✓ Check completed successfully
                      </span>
                      {check.actual_value && (
                        <div className="mt-2">
                          <span className="text-gray-400 text-sm">What the grader found: </span>
                          <code className="text-green-300 text-sm">{check.actual_value}</code>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {/* Execution Summary */}
        {grade.report.metadata && (
          <Card className="bg-gray-800 border-gray-700 mt-8">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Execution Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <span className="text-gray-400 text-sm block">Total Time:</span>
                  <div className="font-mono text-lg text-white">
                    {grade.report.summary?.execution_time?.toFixed(2) || '0.00'}s
                  </div>
                </div>
                {grade.report.metadata.average_check_time !== undefined && (
                  <div>
                    <span className="text-gray-400 text-sm block">Average per Check:</span>
                    <div className="font-mono text-lg text-white">
                      {grade.report.metadata.average_check_time.toFixed(2)}s
                    </div>
                  </div>
                )}
                {grade.report.metadata.ssh_connection_time !== undefined && (
                  <div>
                    <span className="text-gray-400 text-sm block">SSH Connection:</span>
                    <div className="font-mono text-lg text-white">
                      {grade.report.metadata.ssh_connection_time.toFixed(2)}s
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-gray-400 text-sm block">Graded At:</span>
                  <div className="text-lg text-white">
                    {new Date(grade.report.metadata.graded_at || grade.created_at).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex gap-4">
          <Button
            onClick={() => navigate(`/runs/${runId}`)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Lab
          </Button>
        </div>
      </div>
    </div>
  );
}
